import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Network, ZoomIn, ZoomOut, RotateCcw, X,
  Star, Lightbulb, Circle, ChevronRight
} from 'lucide-react';

export default function ConceptMapDialog({ 
  open, 
  onOpenChange, 
  conceptMap, 
  topicName 
}) {
  const [selectedNode, setSelectedNode] = useState(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (open) {
      setSelectedNode(null);
      setZoom(1);
    }
  }, [open]);

  if (!conceptMap) return null;

  const { nodes, central_concept, summary } = conceptMap;

  // SVG dimensions
  const svgWidth = 600;
  const svgHeight = 450;
  const centerX = svgWidth / 2;
  const centerY = svgHeight / 2;

  // Find central node
  const centralNode = nodes.find(n => n.name === central_concept) || nodes[0];
  const otherNodes = nodes.filter(n => n.id !== centralNode?.id);

  // Position nodes in a circle around the center
  const positionedNodes = nodes.map((node, index) => {
    if (node.id === centralNode?.id) {
      return { ...node, x: centerX, y: centerY, radius: 50 };
    }
    
    const otherIndex = otherNodes.findIndex(n => n.id === node.id);
    const totalOther = otherNodes.length;
    const angle = (otherIndex / totalOther) * 2 * Math.PI - Math.PI / 2;
    const orbitRadius = 160;
    
    let nodeRadius = 42;
    if (node.importance === 'core') nodeRadius = 48;
    else if (node.importance === 'supporting') nodeRadius = 38;
    
    return {
      ...node,
      x: centerX + orbitRadius * Math.cos(angle),
      y: centerY + orbitRadius * Math.sin(angle),
      radius: nodeRadius,
      angle
    };
  });

  // Create lookup map for node positions
  const nodePositions = {};
  positionedNodes.forEach(node => {
    nodePositions[node.id] = { x: node.x, y: node.y };
  });

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 'core': return { bg: '#1e293b', light: '#f1f5f9', text: '#1e293b' };
      case 'important': return { bg: '#475569', light: '#f8fafc', text: '#334155' };
      case 'supporting': return { bg: '#64748b', light: '#f8fafc', text: '#475569' };
      default: return { bg: '#94a3b8', light: '#f8fafc', text: '#64748b' };
    }
  };

  const getImportanceIcon = (importance) => {
    switch (importance) {
      case 'core': return <Star className="w-3 h-3" />;
      case 'important': return <Circle className="w-3 h-3" />;
      case 'supporting': return <Lightbulb className="w-3 h-3" />;
      default: return null;
    }
  };

  const getImportanceLabel = (importance) => {
    switch (importance) {
      case 'core': return 'Core Concept';
      case 'important': return 'Key Concept';
      case 'supporting': return 'Supporting';
      default: return 'Concept';
    }
  };

  // Generate unique connections
  const connections = [];
  const connectionSet = new Set();
  
  positionedNodes.forEach(node => {
    node.connections?.forEach(targetId => {
      const connectionKey = [node.id, targetId].sort().join('-');
      if (!connectionSet.has(connectionKey) && nodePositions[targetId]) {
        connectionSet.add(connectionKey);
        connections.push({
          from: node.id,
          to: targetId,
          fromX: node.x,
          fromY: node.y,
          toX: nodePositions[targetId].x,
          toY: nodePositions[targetId].y
        });
      }
    });
  });

  const handleZoomIn = () => setZoom(z => Math.min(1.5, z + 0.1));
  const handleZoomOut = () => setZoom(z => Math.max(0.7, z - 0.1));
  const handleReset = () => {
    setZoom(1);
    setSelectedNode(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden flex flex-col p-0 bg-white [&>button]:hidden">
        {/* Header */}
        <div className="relative bg-slate-900 px-6 py-5">
          {/* Close Button - Explicitly added */}
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          
          <DialogHeader>
            <div className="flex items-start gap-3 pr-10">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Network className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-white text-lg font-bold mb-1">
                  Concept Map
                </DialogTitle>
                <DialogDescription className="text-white/80 text-sm">
                  {topicName}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          {/* Summary */}
          <div className="mt-4 bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-white/90 text-sm leading-relaxed">
              {summary}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-white">
          
          {/* Controls Bar */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
            <div className="flex items-center gap-4 text-xs text-slate-600">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-slate-900"></span>
                Core
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-slate-500"></span>
                Key
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-slate-400"></span>
                Supporting
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomOut}
                className="h-8 w-8 p-0 border-slate-300"
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-xs text-slate-600 w-14 text-center font-medium">
                {Math.round(zoom * 100)}%
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomIn}
                className="h-8 w-8 p-0 border-slate-300"
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                className="h-8 px-3 border-slate-300 ml-2"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset
              </Button>
            </div>
          </div>

          {/* SVG Map Container - Fixed height to show full map */}
          <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100">
            <div 
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
              style={{ 
                height: `${Math.max(450, svgHeight * zoom)}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg 
                viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                style={{
                  width: `${svgWidth * zoom}px`,
                  height: `${svgHeight * zoom}px`,
                  maxWidth: '100%',
                  maxHeight: '100%'
                }}
              >
                {/* Background */}
                <defs>
                  <pattern id="grid" width="30" height="30" patternUnits="userSpaceOnUse">
                    <path d="M 30 0 L 0 0 0 30" fill="none" stroke="#f1f5f9" strokeWidth="1"/>
                  </pattern>
                  <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15"/>
                  </filter>
                  <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="4" result="blur"/>
                    <feMerge>
                      <feMergeNode in="blur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>

                <rect width={svgWidth} height={svgHeight} fill="white" />
                <rect width={svgWidth} height={svgHeight} fill="url(#grid)" />

                {/* Orbit circle */}
                <circle 
                  cx={centerX} 
                  cy={centerY} 
                  r={160} 
                  fill="none" 
                  stroke="#e2e8f0" 
                  strokeWidth="2" 
                  strokeDasharray="8,6"
                />

                {/* Connection lines */}
                {connections.map((conn, idx) => {
                  const isHighlighted = selectedNode && 
                    (selectedNode.id === conn.from || selectedNode.id === conn.to);
                  
                  return (
                    <line
                      key={idx}
                      x1={conn.fromX}
                      y1={conn.fromY}
                      x2={conn.toX}
                      y2={conn.toY}
                      stroke={isHighlighted ? '#1e293b' : '#94a3b8'}
                      strokeWidth={isHighlighted ? 3 : 2}
                      strokeOpacity={isHighlighted ? 1 : 0.6}
                      strokeLinecap="round"
                    />
                  );
                })}

                {/* Nodes */}
                {positionedNodes.map((node) => {
                  const colors = getImportanceColor(node.importance);
                  const isSelected = selectedNode?.id === node.id;
                  const isCentral = node.id === centralNode?.id;
                  
                  return (
                    <g 
                      key={node.id}
                      onClick={() => setSelectedNode(isSelected ? null : node)}
                      style={{ cursor: 'pointer' }}
                      filter={isSelected ? "url(#glow)" : "url(#shadow)"}
                    >
                      {/* Central indicator ring */}
                      {isCentral && (
                        <circle
                          cx={node.x}
                          cy={node.y}
                          r={node.radius + 10}
                          fill="none"
                          stroke="#1e293b"
                          strokeWidth="2"
                          strokeDasharray="6,4"
                          opacity="0.5"
                        />
                      )}
                      
                      {/* Node circle */}
                      <circle
                        cx={node.x}
                        cy={node.y}
                        r={node.radius + (isSelected ? 4 : 0)}
                        fill={colors.bg}
                        stroke={isSelected ? '#0f172a' : 'white'}
                        strokeWidth={isSelected ? 4 : 3}
                      />
                      
                      {/* Node text */}
                      <text
                        x={node.x}
                        y={node.y}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="white"
                        fontSize={isCentral ? "13" : "11"}
                        fontWeight="600"
                        fontFamily="system-ui, -apple-system, sans-serif"
                      >
                        {node.name.length > 14 ? (
                          <>
                            <tspan x={node.x} dy="-0.5em">
                              {node.name.substring(0, Math.ceil(node.name.length / 2)).trim()}
                            </tspan>
                            <tspan x={node.x} dy="1.1em">
                              {node.name.substring(Math.ceil(node.name.length / 2)).trim()}
                            </tspan>
                          </>
                        ) : (
                          node.name
                        )}
                      </text>

                      {/* Core star indicator */}
                      {node.importance === 'core' && !isCentral && (
                        <g>
                          <circle
                            cx={node.x + node.radius - 6}
                            cy={node.y - node.radius + 6}
                            r={10}
                            fill="#fbbf24"
                            stroke="white"
                            strokeWidth="2"
                          />
                          <text
                            x={node.x + node.radius - 6}
                            y={node.y - node.radius + 8}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill="white"
                            fontSize="10"
                          >
                            ★
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>

          {/* Selected Node Details */}
          {selectedNode && (
            <div className="px-4 pb-4 -mt-2">
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-start gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white flex-shrink-0"
                    style={{ backgroundColor: getImportanceColor(selectedNode.importance).bg }}
                  >
                    {getImportanceIcon(selectedNode.importance)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-bold text-slate-900">{selectedNode.name}</h4>
                      <Badge className="bg-slate-900 text-white text-xs">
                        {getImportanceLabel(selectedNode.importance)}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mb-3">
                      {selectedNode.description}
                    </p>
                    
                    {selectedNode.connections?.length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-500">Related:</span>
                        {selectedNode.connections.map(connId => {
                          const connectedNode = nodes.find(n => n.id === connId);
                          if (!connectedNode) return null;
                          return (
                            <Badge 
                              key={connId}
                              variant="outline"
                              className="text-xs cursor-pointer hover:bg-slate-100"
                              onClick={() => setSelectedNode(connectedNode)}
                            >
                              <ChevronRight className="w-3 h-3 mr-0.5" />
                              {connectedNode.name}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Concept List */}
          <div className="px-4 pb-4 border-t border-slate-100 pt-4">
            <h5 className="text-sm font-semibold text-slate-700 mb-3">All Concepts ({nodes.length})</h5>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {positionedNodes.map((node) => {
                const colors = getImportanceColor(node.importance);
                const isSelected = selectedNode?.id === node.id;
                
                return (
                  <button
                    key={node.id}
                    onClick={() => setSelectedNode(isSelected ? null : node)}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border transition-all text-left ${
                      isSelected 
                        ? 'ring-2 ring-slate-400 border-slate-400 bg-slate-100' 
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0"
                      style={{ backgroundColor: colors.bg }}
                    >
                      {getImportanceIcon(node.importance)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-xs truncate">{node.name}</p>
                      <p className="text-[10px] text-slate-500 capitalize">{node.importance}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 text-center mt-4 pt-3 border-t border-slate-100">
              🧠 Concept map generated by FaheemGPT AI
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
